import React from 'react';
import { Button, Card, CardFooter, CardHeader, CustomInput, Table } from 'reactstrap';
import { PaymentDashboardItem } from '../../models/referralModel';
import { IconInbox } from '@tabler/icons-react';
import {
  renderDaysWaitingBadge,
  renderDocAlertBadge,
  renderLineBadge,
} from './referralBadgeStyles';

interface Props {
  items: PaymentDashboardItem[];
  isLoading: boolean;
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onSelectAllIncomplete: () => void;
  onClearSelection: () => void;
  onSendBatchReminders?: () => void;
  isSendingBatchReminders?: boolean;
  onMarkPaidClick: (item: PaymentDashboardItem) => void;
  onSendReminder: (referralId: string) => void;
  isSendingReminderId?: string | null;
  paginationComponent?: React.ReactNode;
}

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    const day = date.getDate();
    const months = [
      'ene',
      'feb',
      'mar',
      'abr',
      'may',
      'jun',
      'jul',
      'ago',
      'sep',
      'oct',
      'nov',
      'dic',
    ];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return dateString;
  }
};

const formatCurrency = (amount?: number | null) => {
  if (amount === undefined || amount === null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const PaymentListTable: React.FC<Props> = ({
  items,
  isLoading,
  selectedIds,
  onToggleSelect,
  onSelectAllIncomplete,
  onClearSelection,
  onSendBatchReminders,
  isSendingBatchReminders,
  onMarkPaidClick,
  onSendReminder,
  isSendingReminderId,
  paginationComponent,
}) => {
  const incompleteCount = items.filter(
    (i) => i.payment_status === 'incomplete_data',
  ).length;

  return (
    <>
      {selectedIds.length > 0 && (
        <div
          className="d-flex justify-content-between align-items-center mb-3 px-3 py-2 text-white"
          style={{
            backgroundColor: '#00c389',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 500,
            boxShadow: '0 2px 6px rgba(0, 195, 137, 0.25)',
          }}
        >
          <div className="d-flex align-items-center">
            <i className="fas fa-check-circle mr-2" />
            <span>
              <strong>{selectedIds.length}</strong>{' '}
              {selectedIds.length === 1
                ? 'referidor con datos pendientes seleccionado'
                : 'referidores con datos pendientes seleccionados'}
            </span>
          </div>
          <Button
            size="sm"
            disabled={isSendingBatchReminders}
            onClick={() => onSendBatchReminders && onSendBatchReminders()}
            style={{
              backgroundColor: '#ffffff',
              color: '#1e293b',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '12px',
              padding: '5px 14px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <i className="fas fa-bell mr-1 text-warning" />
            {isSendingBatchReminders
              ? 'Enviando recordatorios...'
              : 'Enviar recordatorio a seleccionados'}
          </Button>
        </div>
      )}

      <Card
        className="border-0 mb-0"
        style={{
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.04)',
        }}
      >
        <CardHeader
          className="border-0 bg-white py-3 px-4 d-flex justify-content-between align-items-center"
          style={{ borderRadius: '10px 10px 0 0' }}
        >
          <span className="text-muted font-weight-600" style={{ fontSize: '13px' }}>
            {items.length} registros en Dashboard Pagos
          </span>

          {selectedIds.length > 0 ? (
            <Button
              color="link"
              className="p-0 font-weight-600 text-danger"
              style={{ fontSize: '12px', textDecoration: 'none' }}
              onClick={onClearSelection}
            >
              Deseleccionar todos
            </Button>
          ) : incompleteCount > 0 ? (
            <Button
              color="link"
              className="p-0 font-weight-600"
              style={{ fontSize: '12px', textDecoration: 'none', color: '#00875e' }}
              onClick={onSelectAllIncomplete}
            >
              Seleccionar {incompleteCount} incompletos
            </Button>
          ) : null}
        </CardHeader>

        <Table className="align-items-center table-flush mb-0" responsive hover>
          <thead className="thead-light">
            <tr>
              <th scope="col" style={{ width: '40px' }} />
              <th
                scope="col"
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  color: '#8898aa',
                }}
              >
                Referidor
              </th>
              <th
                scope="col"
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  color: '#8898aa',
                  textAlign: 'center',
                  width: '115px',
                }}
              >
                Categoría
              </th>
              <th
                scope="col"
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  color: '#8898aa',
                }}
              >
                Referido
              </th>
              <th
                scope="col"
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  color: '#8898aa',
                  textAlign: 'center',
                  width: '100px',
                }}
              >
                DUI
              </th>
              <th
                scope="col"
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  color: '#8898aa',
                  textAlign: 'center',
                  width: '125px',
                }}
              >
                Datos de pago
              </th>
              <th
                scope="col"
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  color: '#8898aa',
                  textAlign: 'right',
                  width: '95px',
                }}
              >
                Monto
              </th>
              <th
                scope="col"
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  color: '#8898aa',
                  textAlign: 'center',
                  width: '60px',
                }}
              >
                Espera
              </th>
              <th
                scope="col"
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  color: '#8898aa',
                }}
              >
                Fecha máx.
              </th>
              <th
                scope="col"
                className="text-right"
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  color: '#8898aa',
                  width: '130px',
                }}
              >
                Estado
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={10} className="text-center py-4">
                  <div
                    className="spinner-border spinner-border-sm mr-2"
                    style={{ color: '#00c389' }}
                    role="status"
                  />
                  <span className="text-muted font-weight-500" style={{ fontSize: '13px' }}>
                    Cargando dashboard de pagos...
                  </span>
                </td>
              </tr>
            )}

            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-5">
                  <div className="text-muted">
                    <IconInbox size={36} stroke={1.5} className="mb-2 text-gray" />
                    <p className="mb-0 font-weight-bold" style={{ fontSize: '14px' }}>
                      No hay pagos registrados
                    </p>
                  </div>
                </td>
              </tr>
            )}

            {!isLoading &&
              items.map((item) => {
                const isSelected = selectedIds.includes(item.id);
                const isSending = isSendingReminderId === item.id;
                const hasDui = item.referrer.has_dui || !!item.referrer.dui;
                const hasBank =
                  item.referrer.has_bank_account || !!item.referrer.bank_account;

                return (
                  <tr key={item.id}>
                    {/* CHECKBOX */}
                    <td>
                      <CustomInput
                        type="checkbox"
                        id={`chk-${item.id}`}
                        checked={isSelected}
                        onChange={() => onToggleSelect(item.id)}
                      />
                    </td>

                    {/* REFERIDOR */}
                    <td>
                      <span className="font-weight-600 text-dark" style={{ fontSize: '13px' }}>
                        {item.referrer.name || 'Sin nombre'}
                      </span>
                      <div className="text-muted" style={{ fontSize: '11px' }}>
                        {item.referrer.email || 'Sin correo'}
                      </div>
                    </td>

                    {/* CATEGORÍA (Badge estandarizado) */}
                    <td className="text-center">{renderLineBadge(item.category)}</td>

                    {/* REFERIDO */}
                    <td className="font-weight-500 text-dark" style={{ fontSize: '13px' }}>
                      {item.referred_name}
                    </td>

                    {/* DUI (Badge estandarizado) */}
                    <td className="text-center">
                      {renderDocAlertBadge('dui', hasDui, item.referrer.dui)}
                    </td>

                    {/* DATOS DE PAGO (Banco arriba, Cuenta abajo) */}
                    <td className="text-center">
                      {hasBank ? (
                        <div
                          className="d-flex flex-column align-items-center"
                          style={{ gap: '2px' }}
                        >
                          <span
                            className="font-weight-600 text-dark"
                            style={{ fontSize: '12px', lineHeight: '1.2' }}
                          >
                            {item.referrer.bank_name || 'Banco'}
                          </span>
                          <span
                            className="text-muted font-weight-500"
                            style={{ fontSize: '11px', lineHeight: '1.2' }}
                          >
                            {item.referrer.bank_account || 'Cuenta registrada'}
                          </span>
                        </div>
                      ) : (
                        renderDocAlertBadge('bank', false)
                      )}
                    </td>

                    {/* MONTO (Alineado a la derecha, tabular) */}
                    <td className="text-right">
                      <span
                        className="font-weight-bold text-dark"
                        style={{
                          fontSize: '13px',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatCurrency(item.reward_amount)}
                      </span>
                    </td>

                    {/* DÍAS ESPERANDO (Píldora estandarizada) */}
                    <td className="text-center">
                      {renderDaysWaitingBadge(
                        item.days_waiting,
                        item.payment_status === 'paid',
                      )}
                    </td>

                    {/* FECHA MÁX. PAGO */}
                    <td className="text-muted" style={{ fontSize: '12px' }}>
                      {formatDate(item.payment_due_date)}
                    </td>

                    {/* ESTADO / ACCIONES */}
                    <td className="text-right">
                      {item.payment_status === 'paid' ? (
                        <span
                          className="font-weight-600 d-inline-flex align-items-center"
                          style={{ color: '#00875e', fontSize: '12px' }}
                        >
                          <i className="fas fa-check-circle mr-1" />
                          Pagado
                        </span>
                      ) : item.payment_status === 'ready_to_pay' ? (
                        <Button
                          size="sm"
                          style={{
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            padding: '4px 12px',
                            backgroundColor: '#00c389',
                            borderColor: '#00c389',
                            color: '#ffffff',
                            boxShadow: '0 1px 3px rgba(0, 195, 137, 0.3)',
                          }}
                          onClick={() => onMarkPaidClick(item)}
                        >
                          <i className="fas fa-check mr-1" />
                          Marcar pagado
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          disabled={isSending}
                          style={{
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '4px 10px',
                            backgroundColor: '#fffbeb',
                            borderColor: '#fde68a',
                            color: '#b45309',
                          }}
                          onClick={() => onSendReminder(item.id)}
                        >
                          <i className="fas fa-bell mr-1 text-warning" />
                          {isSending ? 'Enviando...' : 'Recordatorio'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>

        {paginationComponent && (
          <CardFooter
            className="py-2 border-0 d-flex justify-content-end bg-white"
            style={{ borderRadius: '0 0 10px 10px' }}
          >
            {paginationComponent}
          </CardFooter>
        )}
      </Card>
    </>
  );
};

export default PaymentListTable;
